import { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react';
import React from 'react';
import CONSTANTS from '../../../../../common/constants/constants';
import errorHandler from '../../../../../common/service/errorHandler';
import { assetManager } from '../../../../../../../common/service/assetManager';
import { INSURANCE_BLOCK_CONFIG } from '../../../../../common/constants/insuranceBlock';
import useWidgetContext from '../../../../../common/react/useWidgetContext';

interface InsuranceBlockDxpProps {
  setLoadingStatus: (status: boolean) => void;
  config: {
    byoCode: string;
  };
}

const InsuranceBlockDxp: FunctionComponent<InsuranceBlockDxpProps> = ({ setLoadingStatus, config }) => {
  const { configuration } = useWidgetContext();
  const widgetId = useMemo(() => 'dxp-insurance-block-container' + Math.floor(Math.random() * 100), []);
  const widgetInstance: any = useRef(null);

  const insuranceBlockLibrary: any = useRef(null);

  // @TODO: to be removed when finance-calculator is using React18
  const removeReactScripts = () => {
    if (assetManager.isScriptLoaded(CONSTANTS.REACT_DEPENDENCIES.react)) {
      assetManager.removeScript(CONSTANTS.REACT_DEPENDENCIES.react, true);
      assetManager.removeScript(CONSTANTS.REACT_DEPENDENCIES.reactDom, true);
      assetManager.removeScript(CONSTANTS.REACT_DEPENDENCIES.react18, true);
      assetManager.removeScript(CONSTANTS.REACT_DEPENDENCIES.reactDom18, true);
    }
  };

  const ensureLibraryLoaded = () => {
    const widgetUrl: string = INSURANCE_BLOCK_CONFIG.dxpWidgetUrl;

    if (assetManager.isScriptLoaded(widgetUrl)) {
      if (!insuranceBlockLibrary.current && (window as any).DXPInsuranceBlock) {
        insuranceBlockLibrary.current = (window as any).DXPInsuranceBlock;
      }

      return Promise.resolve();
    }

    return Promise.resolve()
      .then(() => {
        if (!assetManager.isScriptLoaded(CONSTANTS.REACT_DEPENDENCIES.react18)) {
          return assetManager
            .loadScript(CONSTANTS.REACT_DEPENDENCIES.react18)
            .then(() => assetManager.loadScript(CONSTANTS.REACT_DEPENDENCIES.reactDom18));
        }
      })
      .then(() => assetManager.loadScript(widgetUrl, 'module'))
      .then(() => {
        insuranceBlockLibrary.current = (window as any).DXPInsuranceBlock;
      });
  };

  const getLibrary = useCallback(() => {
    if (!insuranceBlockLibrary.current) {
      throw new Error(`Attempted to use DXP InsuranceBlock library without loading it first`);
    }

    return insuranceBlockLibrary.current;
  }, [insuranceBlockLibrary.current]);

  const initializeWidget = () => {
    if (widgetInstance.current) {
      widgetInstance.current.destroy();
    }

    setLoadingStatus(true);

    const widgetConfig: any = {
      ...config,
      containerId: widgetId,
      dimensions: configuration.dimensions,
    };

    const dxpWidget = getLibrary();
    dxpWidget.createWidget(widgetConfig);
  };

  useEffect(() => {
    if (!widgetInstance.current) {
      removeReactScripts();
      ensureLibraryLoaded()
        .then(() => initializeWidget())
        .catch(errorHandler.promise())
        .finally(() => setLoadingStatus(false));
    }

    return () => {
      if (widgetInstance.current) {
        widgetInstance.current.destroy();
      }
    };
  }, []);

  return <div id={widgetId} />;
};

export default InsuranceBlockDxp;
